import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, restartableTask, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { eq, not, or } from 'ember-truth-helpers';
import CloseButton from 'garaje/components/close-button';
import UiCard from 'garaje/components/ui/card';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import {
  detectOptimalIconScale,
  downloadDataUrl,
  downloadPdf,
  downloadSvg,
  PAGE_DIMENSIONS,
  renderMapToDataUrl,
  renderMapToSvg,
} from 'garaje/utils/export-map';
import type { PdfOptions } from 'garaje/utils/export-map';
import type { LatLngBounds, Map as LeafletMap } from 'leaflet';

interface ExportMapModalSignature {
  Args: {
    leafletMap: LeafletMap;
    bounds: LatLngBounds;
    fileName: string;
    markerNames: Record<string, string>;
    onClose: () => void;
  };
}

// Convert a slider value (-15 to +15) to a scale factor.
// 0 -> 1x (no change), +15 -> 16x, -15 -> 1/16x
function sliderToScale(value: number): number {
  if (value >= 0) return 1 + value;
  return 1 / (1 + Math.abs(value));
}

function scaleToSlider(scale: number): number {
  if (scale >= 1) return scale - 1;
  return -(1 / scale - 1);
}

function formatNumericScale(scale: number): number {
  if (scale >= 1) return Math.round(scale * 10) / 10;
  return Math.round(scale * 100) / 100;
}

const MIN_SCALE = 0.15;
const MAX_SCALE = 15;

/** Accept any positive number during typing */
function parsePositive(val: number): number | null {
  if (!isFinite(val) || val <= 0) return null;
  return val;
}

/** Clamp to allowed range on blur */
function clampScale(val: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, val));
}

export default class ExportMapModal extends Component<ExportMapModalSignature> {
  @tracked iconSlider = 0;
  @tracked labelSlider = 0;
  @tracked previewSvg: string | null = null;
  @tracked previewDataUrl: string | null = null;
  @tracked renderError: string | null = null;
  @tracked downloadMenuOpen = false;
  @tracked pngDialogOpen = false;
  @tracked pngScale = 1;
  @tracked pdfDialogOpen = false;
  @tracked pdfPageSize = 'letter';
  @tracked pdfOrientation: PdfOptions['orientation'] = 'landscape';
  @tracked pdfOutput: PdfOptions['output'] = 'single';
  @tracked pdfFitToPage = true;
  @tracked pdfScale = 1;

  // Pan/zoom state for the preview viewer
  @tracked viewZoom = 1;
  @tracked panX = 0;
  @tracked panY = 0;
  private _dragging = false;
  private _lastMouseX = 0;
  private _lastMouseY = 0;
  private _boundOnMouseMove: ((e: MouseEvent) => void) | null = null;
  private _boundOnMouseUp: (() => void) | null = null;
  private _boundOnWindowBlur: (() => void) | null = null;

  constructor(owner: unknown, args: ExportMapModalSignature['Args']) {
    super(owner, args);
    void this.generatePreview.perform();
  }

  private _autoDetectIconScale(): void {
    const optimalIconScale = detectOptimalIconScale(this.args.leafletMap, this.args.bounds, 1);
    this.iconSlider = scaleToSlider(clampScale(optimalIconScale));
  }

  get isRendering(): boolean {
    return this.generatePreview.isRunning;
  }

  get iconScale(): number {
    return sliderToScale(this.iconSlider);
  }

  get labelScale(): number {
    return sliderToScale(this.labelSlider);
  }

  get iconScaleValue(): number {
    return formatNumericScale(this.iconScale);
  }

  get labelScaleValue(): number {
    return formatNumericScale(this.labelScale);
  }

  get imageStyle(): string {
    const x = this.panX;
    const y = this.panY;
    const z = this.viewZoom;
    return `transform: translate(${x}px, ${y}px) scale(${z}); transform-origin: 0 0; max-width: none;`;
  }

  get svgWidth(): number {
    const { leafletMap, bounds } = this.args;
    const zoom = leafletMap.getZoom();
    const sw = leafletMap.project(bounds.getSouthWest(), zoom);
    const ne = leafletMap.project(bounds.getNorthEast(), zoom);
    return Math.round(Math.abs(ne.x - sw.x));
  }

  get svgHeight(): number {
    const { leafletMap, bounds } = this.args;
    const zoom = leafletMap.getZoom();
    const sw = leafletMap.project(bounds.getSouthWest(), zoom);
    const ne = leafletMap.project(bounds.getNorthEast(), zoom);
    return Math.round(Math.abs(ne.y - sw.y));
  }

  get pngWidth(): number {
    return Math.round(this.svgWidth * this.pngScale);
  }

  get pngHeight(): number {
    return Math.round(this.svgHeight * this.pngScale);
  }

  get pageSizeOptions(): Record<string, string> {
    const opts: Record<string, string> = {};
    for (const [key, val] of Object.entries(PAGE_DIMENSIONS)) {
      opts[key] = val.label;
    }
    return opts;
  }

  private get _pdfContentArea() {
    const page = PAGE_DIMENSIONS[this.pdfPageSize] || PAGE_DIMENSIONS['letter']!;
    const pageW = this.pdfOrientation === 'landscape' ? page.h : page.w;
    const pageH = this.pdfOrientation === 'landscape' ? page.w : page.h;
    const margin = 12.7; // 0.5 inch — standard printer minimum
    return { contentW: pageW - margin * 2, contentH: pageH - margin * 2 };
  }

  get pdfWidthMm(): number {
    const { contentW, contentH } = this._pdfContentArea;
    if (this.pdfOutput === 'single' && this.pdfFitToPage) {
      const fitScale = Math.min(contentW / this.svgWidth, contentH / this.svgHeight);
      return Math.round(this.svgWidth * fitScale);
    }
    if (this.pdfOutput === 'single') {
      return Math.round(this.svgWidth / (150 / 25.4));
    }
    return Math.round((this.svgWidth * this.pdfScale) / (150 / 25.4));
  }

  get pdfHeightMm(): number {
    const { contentW, contentH } = this._pdfContentArea;
    if (this.pdfOutput === 'single' && this.pdfFitToPage) {
      const fitScale = Math.min(contentW / this.svgWidth, contentH / this.svgHeight);
      return Math.round(this.svgHeight * fitScale);
    }
    if (this.pdfOutput === 'single') {
      return Math.round(this.svgHeight / (150 / 25.4));
    }
    return Math.round((this.svgHeight * this.pdfScale) / (150 / 25.4));
  }

  get pdfGridCols(): number {
    if (this.pdfOutput === 'single') return 1;
    const { contentW } = this._pdfContentArea;
    return Math.ceil(this.pdfWidthMm / contentW);
  }

  get pdfGridRows(): number {
    if (this.pdfOutput === 'single') return 1;
    const { contentH } = this._pdfContentArea;
    return Math.ceil(this.pdfHeightMm / contentH);
  }

  get pdfPageCount(): number {
    return this.pdfGridCols * this.pdfGridRows;
  }

  get pdfPageLayoutLabel(): string {
    if (this.pdfOutput === 'single') return '1 page';
    const rows = this.pdfGridRows;
    const cols = this.pdfGridCols;
    const total = rows * cols;
    return `${rows} ${rows === 1 ? 'row' : 'rows'} of ${cols} ${cols === 1 ? 'page' : 'pages'} (total: ${total} ${
      total === 1 ? 'page' : 'pages'
    })`;
  }

  private get _renderOpts() {
    return {
      leafletMap: this.args.leafletMap,
      bounds: this.args.bounds,
      iconScale: this.iconScale,
      labelScale: this.labelScale,
      markerNames: this.args.markerNames,
    };
  }

  generatePreview = restartableTask(async () => {
    await timeout(300);
    if (this.isDestroying || this.isDestroyed) return;

    this.renderError = null;
    try {
      const svgString = await renderMapToSvg(this._renderOpts);
      if (this.isDestroying || this.isDestroyed) return;
      this.previewSvg = svgString;
      this.previewDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
      this.viewZoom = 1;
      this.panX = 0;
      this.panY = 0;
    } catch (error) {
      if (this.isDestroying || this.isDestroyed) return;
      this.renderError = error instanceof Error ? error.message : String(error);
    }
  });

  @action
  onIconScaleChange(event: Event): void {
    this.iconSlider = parseFloat((event.target as HTMLInputElement).value);
    void this.generatePreview.perform();
  }

  @action
  onLabelScaleChange(event: Event): void {
    this.labelSlider = parseFloat((event.target as HTMLInputElement).value);
    void this.generatePreview.perform();
  }

  @action
  onIconScaleInput(event: Event): void {
    const scale = parsePositive(parseFloat((event.target as HTMLInputElement).value));
    if (scale === null) return;
    this.iconSlider = scaleToSlider(scale);
    void this.generatePreview.perform();
  }

  @action
  onIconScaleBlur(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = parseFloat(target.value);
    if (!isFinite(raw) || raw <= 0) {
      target.value = String(this.iconScaleValue);
      return;
    }
    this.iconSlider = scaleToSlider(clampScale(raw));
    void this.generatePreview.perform();
  }

  @action
  onLabelScaleInput(event: Event): void {
    const scale = parsePositive(parseFloat((event.target as HTMLInputElement).value));
    if (scale === null) return;
    this.labelSlider = scaleToSlider(scale);
    void this.generatePreview.perform();
  }

  @action
  onLabelScaleBlur(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = parseFloat(target.value);
    if (!isFinite(raw) || raw <= 0) {
      target.value = String(this.labelScaleValue);
      return;
    }
    this.labelSlider = scaleToSlider(clampScale(raw));
    void this.generatePreview.perform();
  }

  @action
  autoFitMarkers(): void {
    this._autoDetectIconScale();
    void this.generatePreview.perform();
  }

  // --- Download actions ---

  @action
  toggleDownloadMenu(): void {
    this.downloadMenuOpen = !this.downloadMenuOpen;
  }

  @action
  downloadAsSvg(): void {
    this.downloadMenuOpen = false;
    if (this.previewSvg) {
      downloadSvg(this.previewSvg, `${this.args.fileName}.svg`);
    }
  }

  @action
  openPngDialog(): void {
    this.downloadMenuOpen = false;
    this.pngScale = 1;
    this.pngDialogOpen = true;
  }

  @action
  closePngDialog(): void {
    this.pngDialogOpen = false;
  }

  @action
  onPngScaleInput(event: Event): void {
    const val = parsePositive(parseFloat((event.target as HTMLInputElement).value));
    if (val !== null) {
      this.pngScale = val;
    }
  }

  @action
  onPngScaleBlur(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = parseFloat(target.value);
    if (!isFinite(raw) || raw <= 0) {
      target.value = String(this.pngScale);
      return;
    }
    this.pngScale = clampScale(raw);
  }

  downloadAsPng = dropTask(async () => {
    this.pngDialogOpen = false;
    this.renderError = null;
    try {
      // Scale icons and labels proportionally with mapScale to preserve
      // the visual ratios from the preview
      const s = this.pngScale;
      const dataUrl = await renderMapToDataUrl({
        ...this._renderOpts,
        mapScale: s,
        iconScale: this.iconScale * s,
        labelScale: this.labelScale * s,
      });
      if (this.isDestroying || this.isDestroyed) return;
      downloadDataUrl(dataUrl, `${this.args.fileName}.png`);
    } catch (error) {
      if (this.isDestroying || this.isDestroyed) return;
      this.renderError = error instanceof Error ? error.message : String(error);
    }
  });

  @action
  openPdfDialog(): void {
    this.downloadMenuOpen = false;
    this.pdfPageSize = 'letter';
    this.pdfOrientation = 'landscape';
    this.pdfOutput = 'single';
    this.pdfFitToPage = true;
    this.pdfScale = 1;
    this.pdfDialogOpen = true;
  }

  @action
  closePdfDialog(): void {
    this.pdfDialogOpen = false;
  }

  @action
  onPdfPageSizeChange(event: Event): void {
    this.pdfPageSize = (event.target as HTMLSelectElement).value;
  }

  @action
  onPdfOrientationChange(event: Event): void {
    this.pdfOrientation = (event.target as HTMLSelectElement).value as PdfOptions['orientation'];
  }

  @action
  onPdfOutputChange(event: Event): void {
    this.pdfOutput = (event.target as HTMLSelectElement).value as PdfOptions['output'];
  }

  @action
  togglePdfFitToPage(): void {
    this.pdfFitToPage = !this.pdfFitToPage;
  }

  @action
  onPdfScaleInput(event: Event): void {
    const val = parsePositive(parseFloat((event.target as HTMLInputElement).value));
    if (val !== null) {
      this.pdfScale = val;
    }
  }

  @action
  onPdfScaleBlur(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = parseFloat(target.value);
    if (!isFinite(raw) || raw <= 0) {
      target.value = String(this.pdfScale);
      return;
    }
    this.pdfScale = clampScale(raw);
  }

  downloadAsPdf = dropTask(async () => {
    this.pdfDialogOpen = false;
    this.renderError = null;
    try {
      const s = this.pdfOutput === 'multiple' ? this.pdfScale : 1;
      await downloadPdf(
        {
          ...this._renderOpts,
          iconScale: this.iconScale * s,
          labelScale: this.labelScale * s,
        },
        {
          pageSize: this.pdfPageSize,
          orientation: this.pdfOrientation,
          output: this.pdfOutput,
          fitToPage: this.pdfFitToPage,
          scale: s,
        },
        `${this.args.fileName}.pdf`,
      );
    } catch (error) {
      if (this.isDestroying || this.isDestroyed) return;
      this.renderError = error instanceof Error ? error.message : String(error);
    }
  });

  // --- Preview pan/zoom ---

  @action
  zoomIn(): void {
    this.viewZoom = Math.min(50, this.viewZoom * 1.5);
  }

  @action
  zoomOut(): void {
    this.viewZoom = Math.max(0.01, this.viewZoom / 1.5);
  }

  @action
  onWheel(event: WheelEvent): void {
    event.preventDefault();
    // Scale zoom factor by delta magnitude for smoother trackpad zooming.
    // Trackpads send small deltas (~1-4), mouse wheels send large deltas (~100).
    const absDelta = Math.min(Math.abs(event.deltaY), 100);
    const factor = 1 + (absDelta / 100) * 0.15;
    const delta = event.deltaY > 0 ? 1 / factor : factor;
    const newZoom = Math.max(0.01, Math.min(50, this.viewZoom * delta));

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const cursorX = event.clientX - rect.left;
    const cursorY = event.clientY - rect.top;

    this.panX = cursorX - (cursorX - this.panX) * (newZoom / this.viewZoom);
    this.panY = cursorY - (cursorY - this.panY) * (newZoom / this.viewZoom);
    this.viewZoom = newZoom;
  }

  @action
  onMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return;
    event.preventDefault();
    this._dragging = true;
    this._lastMouseX = event.clientX;
    this._lastMouseY = event.clientY;

    // Remove any existing listeners before adding new ones to prevent leaks
    this._removeDragListeners();
    this._boundOnMouseMove = this._onMouseMove.bind(this);
    this._boundOnMouseUp = this._onMouseUp.bind(this);
    // Window blur catches the case where mouseup fires off-window (over OS dock,
    // after focus loss) — without it, _dragging stays true and the next
    // mousemove pans without a button held.
    this._boundOnWindowBlur = this._onMouseUp.bind(this);
    window.addEventListener('mousemove', this._boundOnMouseMove);
    window.addEventListener('mouseup', this._boundOnMouseUp);
    window.addEventListener('blur', this._boundOnWindowBlur);
  }

  private _onMouseMove(event: MouseEvent): void {
    if (!this._dragging) return;
    const dx = event.clientX - this._lastMouseX;
    const dy = event.clientY - this._lastMouseY;
    this.panX += dx;
    this.panY += dy;
    this._lastMouseX = event.clientX;
    this._lastMouseY = event.clientY;
  }

  private _onMouseUp(): void {
    this._dragging = false;
    this._removeDragListeners();
  }

  private _removeDragListeners(): void {
    if (this._boundOnMouseMove) {
      window.removeEventListener('mousemove', this._boundOnMouseMove);
      this._boundOnMouseMove = null;
    }
    if (this._boundOnMouseUp) {
      window.removeEventListener('mouseup', this._boundOnMouseUp);
      this._boundOnMouseUp = null;
    }
    if (this._boundOnWindowBlur) {
      window.removeEventListener('blur', this._boundOnWindowBlur);
      this._boundOnWindowBlur = null;
    }
  }

  willDestroy(): void {
    super.willDestroy();
    this.previewDataUrl = null;
    this.previewSvg = null;
    this._removeDragListeners();
  }

  <template>
    <ModalDialog
      @overlayClass="z-1000"
      @targetAttachment="center"
      @translucentOverlay={{true}}
      @containerClass="!max-w-4xl !overflow-visible"
    >
      <CloseButton
        class="right-4 top-4 !bg-white !p-0 w-10 h-10 absolute z-10"
        style="min-width:0"
        data-test-close-export-modal
        @onClick={{@onClose}}
      />

      <div class="p-6 relative">
        <h1 class="text-2xl font-bold mb-4" data-test-export-modal-title>Export map</h1>

        <UiCard class="mb-4 !border-carbon-20" data-test-options-panel>
          <div class="flex items-center justify-between mb-3">
            <div class="text-base font-semibold text-carbon-70">Scale Options</div>
            <button
              class="text-base text-gem-50 hover:text-gem-60"
              type="button"
              data-test-auto-fit-markers
              {{on "click" this.autoFitMarkers}}
            >
              Auto-fit icons
            </button>
          </div>
          <div class="space-y-3">
            <div class="flex items-center gap-3">
              <span class="text-base text-carbon-60 shrink-0" style="width:90px">Icon scale</span>
              <input
                type="range"
                min="-5.7"
                max="14"
                step="0.1"
                value={{this.iconSlider}}
                class="flex-1"
                data-test-icon-scale-input
                {{on "input" this.onIconScaleChange}}
              />
              <input
                type="text"
                value={{this.iconScaleValue}}
                class="text-base text-carbon-60 text-center border border-carbon-20 rounded shrink-0"
                style="width:70px;height:30px"
                data-test-icon-scale-value
                {{on "input" this.onIconScaleInput}}
                {{on "blur" this.onIconScaleBlur}}
              />
            </div>
            <div class="flex items-center gap-3">
              <span class="text-base text-carbon-60 shrink-0" style="width:90px">Label scale</span>
              <input
                type="range"
                min="-5.7"
                max="14"
                step="0.1"
                value={{this.labelSlider}}
                class="flex-1"
                data-test-label-scale-input
                {{on "input" this.onLabelScaleChange}}
              />
              <input
                type="text"
                value={{this.labelScaleValue}}
                class="text-base text-carbon-60 text-center border border-carbon-20 rounded shrink-0"
                style="width:70px;height:30px"
                data-test-label-scale-value
                {{on "input" this.onLabelScaleInput}}
                {{on "blur" this.onLabelScaleBlur}}
              />
            </div>
          </div>
        </UiCard>

        <UiCard
          class="relative !p-0 !bg-carbon-10 !border-carbon-20"
          style="height: 450px; cursor: grab;"
          data-test-preview-container
          {{! template-lint-disable no-pointer-down-event-binding }}
          {{on "mousedown" this.onMouseDown}}
          {{on "wheel" this.onWheel passive=false}}
        >
          <div class="absolute top-2 right-2 z-10 flex flex-col gap-1">
            <button
              class="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-lg text-carbon-60 hover:bg-carbon-10"
              type="button"
              data-test-zoom-in
              {{on "click" this.zoomIn}}
            >
              +
            </button>
            <button
              class="w-8 h-8 bg-white rounded shadow flex items-center justify-center text-lg text-carbon-60 hover:bg-carbon-10"
              type="button"
              data-test-zoom-out
              {{on "click" this.zoomOut}}
            >
              &minus;
            </button>
          </div>
          {{#if this.isRendering}}
            <div class="absolute inset-0 flex items-center justify-center">
              <LoadingSpinnerPulse class="w-8 h-8" />
              <span class="ml-2 text-carbon-60">Generating preview…</span>
            </div>
          {{else if this.previewDataUrl}}
            <img
              src={{this.previewDataUrl}}
              alt="Map preview"
              class="absolute"
              style={{this.imageStyle}}
              draggable="false"
              data-test-preview-image
            />
          {{else if this.renderError}}
            <div class="absolute inset-0 flex items-center justify-center text-red-40">
              Preview failed:
              {{this.renderError}}
            </div>
          {{/if}}
        </UiCard>

        <div class="flex justify-end items-center mt-4">
          <div class="relative">
            <div class="flex">
              <button
                class="btn btn--brand"
                style="border-top-right-radius:0;border-bottom-right-radius:0"
                disabled={{or this.isRendering (not this.previewDataUrl)}}
                data-test-download-button
                type="button"
                {{on "click" this.downloadAsSvg}}
              >
                {{#if this.isRendering}}
                  Generating…
                {{else}}
                  Download
                {{/if}}
              </button>
              <button
                class="btn btn--brand"
                style="border-top-left-radius:0;border-bottom-left-radius:0;border-left:1px solid rgba(255,255,255,0.3);padding-left:8px;padding-right:8px"
                disabled={{or this.isRendering (not this.previewDataUrl)}}
                type="button"
                data-test-download-menu-toggle
                {{on "click" this.toggleDownloadMenu}}
              >
                &#9660;
              </button>
            </div>
            {{#if this.downloadMenuOpen}}
              {{! Invisible overlay to close menu on click-outside }}
              <div class="fixed inset-0 z-40" {{on "click" this.toggleDownloadMenu}}></div>
              <div
                class="absolute right-0 bottom-full mb-1 bg-white border border-carbon-20 rounded shadow-lg z-50"
                style="min-width:160px"
              >
                <button
                  class="block w-full text-left px-3 py-2 text-sm hover:bg-carbon-5"
                  type="button"
                  data-test-download-svg
                  {{on "click" this.downloadAsSvg}}
                >
                  Download SVG
                </button>
                <button
                  class="block w-full text-left px-3 py-2 text-sm hover:bg-carbon-5"
                  type="button"
                  data-test-download-png
                  {{on "click" this.openPngDialog}}
                >
                  Download PNG
                </button>
                <button
                  class="block w-full text-left px-3 py-2 text-sm hover:bg-carbon-5"
                  type="button"
                  data-test-download-pdf
                  {{on "click" this.openPdfDialog}}
                >
                  Download PDF
                </button>
              </div>
            {{/if}}
          </div>
        </div>

        {{! PNG download dialog — rendered as overlay inside the main modal }}
        {{#if this.pngDialogOpen}}
          <div class="absolute inset-0 z-50 flex items-center justify-center" style="background:rgba(0,0,0,0.3)">
            <div
              class="bg-white rounded-lg shadow-lg p-6"
              style="max-width:440px;width:100%"
              role="dialog"
              aria-modal="true"
              aria-labelledby="png-dialog-title"
            >
              <h2 id="png-dialog-title" class="text-lg font-bold mb-5">Download as PNG</h2>
              <div class="space-y-4">
                <div class="flex items-center justify-between">
                  <span class="text-base text-carbon-60">Image Dimensions</span>
                  <span class="text-base text-carbon-80">{{this.svgWidth}} &times; {{this.svgHeight}} px</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-base text-carbon-60">Scale Factor</span>
                  <input
                    type="text"
                    value={{this.pngScale}}
                    class="text-base text-carbon-60 text-center border border-carbon-20 rounded"
                    style="width:80px;height:32px"
                    data-test-png-scale-input
                    {{on "input" this.onPngScaleInput}}
                    {{on "blur" this.onPngScaleBlur}}
                  />
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-base text-carbon-60">Download Dimensions</span>
                  <span class="text-base text-carbon-80">{{this.pngWidth}} &times; {{this.pngHeight}} px</span>
                </div>
              </div>
              <div class="flex justify-end gap-2 mt-5">
                <button class="btn btn--outline" type="button" {{on "click" this.closePngDialog}}>
                  Cancel
                </button>
                <button
                  class="btn btn--brand"
                  type="button"
                  data-test-png-download-button
                  {{on "click" (perform this.downloadAsPng)}}
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        {{/if}}

        {{! PDF download dialog — rendered as overlay inside the main modal }}
        {{#if this.pdfDialogOpen}}
          <div class="absolute inset-0 z-50 flex items-center justify-center" style="background:rgba(0,0,0,0.3)">
            <div
              class="bg-white rounded-lg shadow-lg p-6"
              style="max-width:480px;width:100%"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pdf-dialog-title"
            >
              <h2 id="pdf-dialog-title" class="text-lg font-bold mb-5">Download as PDF</h2>
              <div class="space-y-4">
                <div class="flex items-center justify-between">
                  <span class="text-base text-carbon-60">Image Dimensions</span>
                  <span class="text-base text-carbon-80">{{this.svgWidth}} &times; {{this.svgHeight}} px</span>
                </div>
                <table style="width:100%;border-collapse:collapse">
                  <colgroup>
                    <col style="width:120px" />
                    <col />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td class="text-base text-carbon-60 pr-3 py-2" style="vertical-align:middle">Page Size</td>
                      <td class="py-2" style="vertical-align:middle">
                        <select
                          class="text-base text-carbon-60 border border-carbon-20 rounded px-2"
                          style="height:35px;width:100%"
                          data-test-pdf-page-size
                          {{on "change" this.onPdfPageSizeChange}}
                        >
                          {{#each-in this.pageSizeOptions as |key label|}}
                            <option value={{key}} selected={{eq this.pdfPageSize key}}>{{label}}</option>
                          {{/each-in}}
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-base text-carbon-60 pr-3 py-2" style="vertical-align:middle">Orientation</td>
                      <td class="py-2" style="vertical-align:middle">
                        <select
                          class="text-base text-carbon-60 border border-carbon-20 rounded px-2"
                          style="height:35px;width:100%"
                          data-test-pdf-orientation
                          {{on "change" this.onPdfOrientationChange}}
                        >
                          <option value="landscape" selected={{eq this.pdfOrientation "landscape"}}>Landscape</option>
                          <option value="portrait" selected={{eq this.pdfOrientation "portrait"}}>Portrait</option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-base text-carbon-60 pr-3 py-2" style="vertical-align:middle">Output</td>
                      <td class="py-2" style="vertical-align:middle">
                        <select
                          class="text-base text-carbon-60 border border-carbon-20 rounded px-2"
                          style="height:35px;width:100%"
                          data-test-pdf-output
                          {{on "change" this.onPdfOutputChange}}
                        >
                          <option value="single" selected={{eq this.pdfOutput "single"}}>Single Page</option>
                          <option value="multiple" selected={{eq this.pdfOutput "multiple"}}>Multiple Pages</option>
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
                {{#if (eq this.pdfOutput "single")}}
                  <div class="flex items-center justify-between">
                    <span class="text-base text-carbon-60">Fit to page</span>
                    <input
                      type="checkbox"
                      checked={{this.pdfFitToPage}}
                      data-test-pdf-fit-to-page
                      {{on "change" this.togglePdfFitToPage}}
                    />
                  </div>
                {{else}}
                  <div class="flex items-center justify-between">
                    <span class="text-base text-carbon-60">Scale Factor</span>
                    <input
                      type="text"
                      value={{this.pdfScale}}
                      class="text-base text-carbon-60 text-center border border-carbon-20 rounded"
                      style="width:80px;height:32px"
                      data-test-pdf-scale-input
                      {{on "input" this.onPdfScaleInput}}
                      {{on "blur" this.onPdfScaleBlur}}
                    />
                  </div>
                {{/if}}
                <div class="flex items-center justify-between">
                  <span class="text-base text-carbon-60">Download Dimensions</span>
                  <span class="text-base text-carbon-80 text-right">
                    {{this.pdfWidthMm}}
                    &times;
                    {{this.pdfHeightMm}}
                    mm
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-base text-carbon-60">Page Layout</span>
                  <span class="text-base text-carbon-80">{{this.pdfPageLayoutLabel}}</span>
                </div>
              </div>
              <div class="flex justify-end gap-2 mt-5">
                <button class="btn btn--outline" type="button" {{on "click" this.closePdfDialog}}>
                  Cancel
                </button>
                <button
                  class="btn btn--brand"
                  type="button"
                  data-test-pdf-download-button
                  {{on "click" (perform this.downloadAsPdf)}}
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        {{/if}}
      </div>
    </ModalDialog>
  </template>
}
